import { useRoute, useLocation } from "wouter";
import { useGetLead, getGetLeadQueryKey } from "@workspace/api-client-react";
import { ConvertLeadForm } from "@/components/accounts/convert-lead-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

/**
 * 2026-06-10 — Convert Lead page route.
 *
 * Replaces the prior side-Sheet UX. Both entry points (the Accounts
 * page "Add Account" picker and the Lead detail "Convert to Member"
 * button) now navigate here instead of opening a Sheet, so the
 * conversion flow gets the full viewport — operators have repeatedly
 * scrolled past the bottom of the Sheet with the Plan picker open.
 *
 * URL: /accounts/convert/:leadId
 *
 * Fetches the lead by id, renders ConvertLeadForm. Loading + 404
 * states handled here so the form doesn't need to know about routing.
 */
export default function ConvertLeadPage() {
  const [match, params] = useRoute<{ leadId: string }>("/accounts/convert/:leadId");
  const [, navigate] = useLocation();
  const leadId = match ? Number(params.leadId) : NaN;

  const { data: lead, isLoading, isError } = useGetLead(leadId, {
    query: {
      enabled: Number.isFinite(leadId) && leadId > 0,
      queryKey: getGetLeadQueryKey(leadId),
    },
  });

  if (!match || !Number.isFinite(leadId)) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">Invalid lead id.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="space-y-3 pt-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !lead) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/accounts")}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Accounts
        </Button>
        <div className="rounded-md border bg-muted/30 p-6 text-sm text-muted-foreground">
          Lead #{leadId} could not be loaded. It may have been deleted or you
          may not have access to it.
        </div>
      </div>
    );
  }

  return (
    <ConvertLeadForm
      lead={{
        id: lead.id,
        customerName: lead.customerName,
        mobileNumber: lead.mobileNumber,
        email: lead.email,
      }}
      backHref="/accounts"
    />
  );
}
